import { useCallback, useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import { useGoogleOneTapLogin, type CredentialResponse, type PromptMomentNotification } from "@react-oauth/google"
import { useAppDependencies } from "src/context/AppContext"
import StateConstants from "src/states/StateContstants"
import { applyUserEntitlements } from "src/utils/LocalDevAccess"
import posthog from "src/consent/posthog"
import { MAGIC_LINK_PATH } from "src/utils/magicLink"

const LOGIN_METHOD = "google_one_tap"

/**
 * Paths that already render the full login form. google.accounts.id.initialize() is a
 * global singleton, so One Tap must stay disabled wherever the Google sign-in button
 * lives or the two clobber each other's callback. The report-email link page signs the reader
 * in itself, and a One Tap sign-in landing in the middle of that would swap the account under it.
 */
const SUPPRESSED_PATHS = ["/login", MAGIC_LINK_PATH]

/**
 * Renders nothing itself — Google injects the prompt into its own top-right container.
 *
 * Mounted once at the app root so every logged-out visitor gets the prompt on page
 * load, on whichever page they landed on.
 */
export default function GoogleOneTapPrompt() {
    const { apiHandler, loginHelper, stateHandler } = useAppDependencies()
    const location = useLocation()

    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => !!stateHandler.get(StateConstants.IS_LOGGED_IN))
    const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(() => !!stateHandler.get(StateConstants.SHOW_LOGIN_MODAL))

    useEffect(() => {
        const onLoggedInChange = (value: boolean) => setIsLoggedIn(!!value)
        const onLoginModalChange = (value: boolean) => setIsLoginModalOpen(!!value)

        stateHandler.addListener(StateConstants.IS_LOGGED_IN, onLoggedInChange)
        stateHandler.addListener(StateConstants.SHOW_LOGIN_MODAL, onLoginModalChange)
        return () => {
            stateHandler.removeListener(StateConstants.IS_LOGGED_IN, onLoggedInChange)
            stateHandler.removeListener(StateConstants.SHOW_LOGIN_MODAL, onLoginModalChange)
        }
    }, [stateHandler])

    const onSuccess = useCallback(async (credentialResponse: CredentialResponse) => {
        if (!credentialResponse?.credential) return

        try {
            await loginHelper.signInWithGoogleIdToken(credentialResponse.credential, LOGIN_METHOD)
        } catch (error) {
            console.error("Google One Tap sign-in failed:", error)
            posthog?.capture("google_one_tap_sign_in_failed")
            return
        }

        apiHandler.clearCache()
        try {
            const userInfo = await apiHandler.getUserInfo()
            applyUserEntitlements(
                stateHandler,
                userInfo?.features || [],
                userInfo?.at || "basic",
                userInfo?.free_detailed_valuation_credits_remaining ?? 0,
                userInfo?.free_unlocked_property_ids ?? [],
                userInfo?.permissions ?? [],
                userInfo?.role ?? null
            )
        } catch (error) {
            applyUserEntitlements(stateHandler, [], "basic", 0, [], [], null)
        }
    }, [apiHandler, loginHelper, stateHandler])

    const onError = useCallback(() => {
        posthog?.capture("google_one_tap_credential_error")
    }, [])

    const promptMomentNotification = useCallback((notification: PromptMomentNotification) => {
        if (notification.isDisplayed()) {
            posthog?.capture("google_one_tap_displayed")
        } else if (notification.isNotDisplayed()) {
            // Surfaces setup problems such as unregistered_origin or invalid_client.
            posthog?.capture("google_one_tap_not_displayed", { reason: notification.getNotDisplayedReason() })
        } else if (notification.isSkippedMoment()) {
            posthog?.capture("google_one_tap_skipped", { reason: notification.getSkippedReason() })
        } else if (notification.isDismissedMoment()) {
            posthog?.capture("google_one_tap_dismissed", { reason: notification.getDismissedReason() })
        }
    }, [])

    const disabled = isLoggedIn
        || isLoginModalOpen
        || SUPPRESSED_PATHS.includes(location.pathname)

    useGoogleOneTapLogin({
        onSuccess,
        onError,
        promptMomentNotification,
        disabled,
        // Chrome requires FedCM for One Tap now that third-party cookies are restricted.
        use_fedcm_for_prompt: true,
        // The homepage leads with a search box; a tap-outside cancel would kill the
        // prompt the moment someone starts typing.
        cancel_on_tap_outside: false,
        // Returning visitors are signed back in without a tap. Google only auto-selects
        // for accounts that have already granted consent to this client ID, so a
        // first-time visitor still gets the explicit consent step. LoginHelper.logout()
        // calls googleLogout() to stop this re-authenticating anyone who just signed out.
        auto_select: true,
    })

    return null
}
